import React, { useState, useEffect, memo, useContext, useImperativeHandle } from 'react';
import PaymentDetail from '../payment/PaymentDetail';
import { LoadingContext } from '../../contexts/loadingContext';
import Axios from 'axios';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement
} from "@stripe/react-stripe-js";
import useWindowDimensions from '../../custom_hooks/useWindowDimensions';
import { useTranslation } from 'react-i18next';

const STRIPE_STYLE = {
  base: {
      color: '#32325d',
      lineHeight: '18px',
      fontSmoothing: 'antialiased',
      fontSize: '15px',
      '::placeholder': {
          color: '#aab7c4'
      }
  },
  invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
  },
};

const PaymentStripeForm = memo((props) => {
  const {
    uid,
    formURL,
    showErrorToastr,
    acceptPayment,
    paymentSettings,
    color,
    formRef,
    elements,
    buttonFontColor,
    modalConfirmBgColor,
    modalConfirmButtonOver,
    modalConfirmButtonOut,
    populateFormData,
    createNewAppointmentAPI,
    changeShowConfirmationComponent,
    successCreateAppointment,
    handleBackButton,
    handleFormSubmitRef
  } = props;

  const stripe = useStripe();
  const stripeElements = useElements();
  const [ isLoading, setIsLoading ] = useState(false);
  const { width } = useWindowDimensions();
  // to show loading overlay
  const { showLoading, hideLoading } = useContext(LoadingContext);
  const { t } = useTranslation();

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if(isLoading){
      showLoading(color);
    }
    else{
      hideLoading();
    }
  },
  // eslint-disable-next-line
  [isLoading]);

  useImperativeHandle(handleFormSubmitRef, () => ({
    submitForm(e){
      handleSubmit(e);
    }
  }));

  // generate amount if payment is required
  const amountFormatted = () => {
    if(!paymentSettings) return 0;

    let amount = 0;
    amount = (Number(paymentSettings.price)/100).toFixed(2);

    let priceSplit = amount.split(".");
    if(priceSplit.length > 1){
        priceSplit[0] = priceSplit[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    }
    amount = priceSplit.join(".");
    return amount;
  };

  const createAppointment = (result) => {
    let data = populateFormData();
    data['charge'] = {}; // for stripe

    if (result.success) {
      data['charge']['payment_intent_id'] = result['payment_intent_id'];
      data['charge']['payment_method'] = result['payment_method'];
    } else {
      data['charge']['payment_intent_id'] = result['paymentIntent']['id'];
      data['charge']['payment_method'] = result['paymentIntent']['payment_method'];
    }

    createNewAppointmentAPI(data).then( async (result) => {
      if(result && result['status'] === 'success'){
        setIsLoading(false);
        await changeShowConfirmationComponent();
        successCreateAppointment();
      } else {
        showErrorToastr(result.info)
      }
    });
  }

  const handleSubmit = async(e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!stripe || !stripeElements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = stripeElements.getElement(CardNumberElement);

    stripe.createPaymentMethod({
      type: 'card',
      card: cardElement
    }).then(function(result) {
      if (result.error) {
        showErrorToastr(result.error.message)
        setIsLoading(false);
      } else {
        Axios(`${formURL}/create_payment`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            payment_method_id: result.paymentMethod.id,
            event_id: uid
          }
        }).then((response) => {
          // Handle server response
          handleServerResponse(response);
        }).catch(error=>{
          showErrorToastr(error.message);
          setIsLoading(false);
        });

      }
    })

    function handleServerResponse(response) {
      let dataResponse = response.data
      if (dataResponse.error) {
        setIsLoading(false);
      } else if (dataResponse.requires_action) {
        // Use Stripe.js to handle required card action
        console.log('require action')
        handleAction(dataResponse);
      } else {
        console.log('success handle server response')
        createAppointment(dataResponse);
      }
    }

    function handleAction(response) {
      stripe.handleCardAction(
        response.payment_intent_client_secret
      ).then(function(result) {
        if (result.error) {
          showErrorToastr(result.error.message)
          setIsLoading(false);
        } else {
          createAppointment(result);
        }
      });
    }
  }

  // show the form
  return(
    <div className="form-fields-wrap">
      <form ref={formRef} id="form-submit" onSubmit = {(e) => handleSubmit(e) }>

        <div className="content-form">
          <div className="header-form">
            Fill in your details
          </div>

          { elements }

          { acceptPayment && (
            <div id="payment-meeting">
              <div className="payments-detail-wrap">
                <div className="title-payment">
                  { t('Payment Information') }
                </div>

                <div className="payments-detail">
                  <PaymentDetail paymentSettings={paymentSettings} amount={amountFormatted} />

                  { acceptPayment === 'stripe' && (
                    <div className="stripe-card-elements">
                        <div className="stripe-card-number">
                            <label>{ t('Card Number') }</label>
                            <CardNumberElement showIcon={true} options={{style: STRIPE_STYLE, showIcon: true}} />
                        </div>

                        <div className="stripe-card-expiry-cvc">
                            <div className="stripe-card-expiry">
                                <label>{ t('Card Expiry') }</label>
                                <CardExpiryElement options={{style: STRIPE_STYLE}} />
                            </div>

                            <div className="stripe-card-cvc">
                                <label>{ t('CVC') }</label>
                                <CardCvcElement options={{style: STRIPE_STYLE}} />
                            </div>
                        </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="button-confirm-wrap">
          { width > 858 && (
            <>
              <input
                type="submit"
                value={isLoading ? t("Loading") : t("Confirm")}
                className="confirm-button"
                style={{backgroundColor: modalConfirmBgColor, outline: color, color: buttonFontColor }}
                disabled={isLoading}
                onMouseOver={modalConfirmButtonOver}
                onMouseOut={modalConfirmButtonOut} />

              <button
                onClick={() => handleBackButton()}
                className="back-button"
                style={{ color: color }} >
                { t('Go Back') }
              </button>
            </>
          )}
        </div>

      </form>
    </div>
  );


});

export default (PaymentStripeForm);
